import styled from 'styled-components';
import { Header } from './components/header';
import { Form, useForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { useParams } from 'react-router-dom';
import { useGetOfflineFirstSectionsQuery } from '../../services/api/vehicle-sections';
import { SectionsList } from './SectionsList';
import { CameraView } from './CameraView';
import { CameraViewContextProvider, useCameraViewContext } from './CameraView/context';
import { ImageNotesDialog } from './ImageNotesDialog';
import { CircularProgress } from '@material-ui/core';
import { useGetVehicleByIdQuery } from 'services/api/upload-vehicle';
import { useInitialFormValues } from './hooks/useInitialFormValues';
import { Banner } from './Banners';
import { SocketMessage } from 'services/api/types';
import { WS_AI_ANALYSIS, WS_AI_TIRES } from 'constants/actionTypes';
import { SectionImage } from 'services/api/vehicle-sections/types';
import React, { useEffect, useState } from 'react';
import getSocket from 'services/socket';
import { useFormVehicleId } from './hooks/useFormVehicleId';
import { useVideoUploadSocket } from './hooks/useVideoUploadSocket';
import { useUpdateFormAfterSync } from './hooks/useUpdateFormAfterSync';
import { ConcurrentEditBanner } from './Banners/ConcurrentUpdateBanner';
import { VehicleEditingProvider } from 'context/isAnotherUserEditingContext';
import { VehicleInfoTabs } from './VehicleInfoTabs';
import { VinInfoTab } from './VinInfoTab';
import { VehicleInfoTab } from './VehicleInfoTab';
import { useVehicleUploadTabsContext, VehicleUploadTabsContextProvider } from './context';
import { ListExpandContextProvider } from './context/ListExpandContext';
import { getIn } from 'final-form';

const Wrapper = styled.div<{ $hasTab: boolean }>`
  padding: ${props => (props.$hasTab ? '72px 16px 16px' : '72px 16px 52px')};
  background: #fff;
  display: flex;
  flex-direction: column;
  flex: 1 1 100%;
`;

const LoadingWrapper = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
`;

const handleSubmit = () => {};

const ListerVehicleUploadV2 = () => {
  const { id: vehicleId } = useParams<{ id: string }>();

  const { data: vehicle, isLoading: vehicleLoading } = useGetVehicleByIdQuery(Number(vehicleId));
  const { isLoading } = useGetOfflineFirstSectionsQuery(Number(vehicleId), { skip: !vehicleId });

  const initialValues = useInitialFormValues();

  if (!vehicle || isLoading || vehicleLoading) {
    return (
      <LoadingWrapper>
        <CircularProgress />
      </LoadingWrapper>
    );
  }

  return (
    <Form
      mutators={{
        ...arrayMutators
      }}
      onSubmit={handleSubmit}
      initialValues={initialValues}
      subscription={{ submitting: true }}
    >
      {() => (
        <VehicleEditingProvider vehicle={vehicle}>
          <VehicleUploadTabsContextProvider>
            <CameraViewContextProvider>
              <ListExpandContextProvider>
                <RenderForm />
                <Banner />
                <ConcurrentEditBanner />
              </ListExpandContextProvider>
            </CameraViewContextProvider>
          </VehicleUploadTabsContextProvider>
        </VehicleEditingProvider>
      )}
    </Form>
  );
};

type TireAnalysisPayload = {
  imageId: number;
  sectionId: number;
  depth: number;
  grade: string;
          sectionPath: string;
}

const RenderForm = () => {
  const { isOpened } = useCameraViewContext();
  const vehicleId = useFormVehicleId();
  const form = useForm();

  const { selectedTab } = useVehicleUploadTabsContext();

  useVideoUploadSocket();

  useUpdateFormAfterSync(vehicleId);

  const listener = ({ payload, type }: SocketMessage) => {
    if (type === WS_AI_ANALYSIS) {
      const values = form.getState().values;
      const { section, notes, imageId } = payload;
      const images = getIn(values, `${section}.images`) || [];
      const index = images.findIndex((img: SectionImage) => img.id === imageId);
      if (index === -1) return;
      form.change(`${section}.images.${index}.notes`, notes);
    }

    if (type === WS_AI_TIRES) {
      const values = form.getState().values;
      console.log(payload, 'WS_AI_TIREs');
      const { sectionPath, grade, depth, imageId } = payload as TireAnalysisPayload;
      const images = getIn(values, `exterior.subsections.tires.images`) || []
      const index = images.findIndex((img: SectionImage) => img.id === imageId);
      if (index === -1) return;
      form.change(`exterior.subsections.tires.images.${index}.grade`, depth);
      form.change(sectionPath, grade)
    }
  };

  useEffect(() => {
    if (!vehicleId) return;
    const socket = getSocket();

    socket.emit('subscribe', {
      prefix: `vehicles-ai-updates`,
      ids: [vehicleId]
    });

    socket.on('message', listener);

    return () => {
      socket.emit('unsubscribe', {
        prefix: `vehicles-ai-updates`,
        ids: [vehicleId]
      });
      socket.off('message', listener);
    };
  }, [vehicleId, getSocket]);

  if (isOpened) {
    return <CameraView />;
  }

  return (
    <Wrapper $hasTab={selectedTab !== null}>
      <Header />
      <VehicleInfoTabs />
      <MainContent />
      <ImageNotesDialog />
    </Wrapper>
  );
};

const MainContent = () => {
  const { selectedTab } = useVehicleUploadTabsContext();

  if (selectedTab === 'vin_info') return <VinInfoTab />;
  if (selectedTab === 'vehicle_info') return <VehicleInfoTab />;
  return <SectionsList />;
};

export default ListerVehicleUploadV2;
